import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Paper,
  Fade,
  Chip,
  List,
  ListItem,
  ListItemText,
  CardActions,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Pagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useTheme, alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import InvoiceDetailModal from './InvoiceDetailModal';
import AccountingEntriesModal from './AccountingEntriesModal';
import AccountOverrideForm from './AccountOverrideForm';
import InvoiceCardItem from './InvoiceCardItem';
import useSupabaseClient from '../hooks/useSupabaseClient';
import { isDemoAccount, formatColombianCurrency, formatColombianDate } from '../lib/demoAccount.mjs';
import ColombianDateField from './ColombianDateField';

const getStatusColor = (status, theme) => {
  switch (status) {
    case 'APROBADO':
      return theme.palette.success.main;
    case 'SINCRONIZADO':
      return theme.palette.info.main; // Using info color for synchronized
    case 'ERROR':
    case 'DESAPROBADO':
      return theme.palette.error.main;
    case 'PENDIENTE':
      return theme.palette.warning.main;
    default:
      return theme.palette.grey[500];
  }
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => ({
  borderLeft: `4px solid ${getStatusColor(status, theme)}`, // Dynamic border color
  // cursor: 'pointer', // Removed as actions are now button-driven
  transition: 'all 0.3s ease',

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(5),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: '400px',
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const BULK_INVOICE_TYPE_STORAGE_KEY = 'invoice_bulk_invoice_type';
const BULK_CUSTOM_DOC_TYPE_STORAGE_KEY = 'invoice_bulk_custom_doc_type';



const DebouncedSearchInput = ({ initialValue, onSearchChange }) => {
  const [localTerm, setLocalTerm] = useState(initialValue || '');

  useEffect(() => {
    setLocalTerm(initialValue || '');
  }, [initialValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (initialValue !== localTerm) {
        onSearchChange(localTerm);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localTerm, initialValue, onSearchChange]);

  return (
    <TextField
      fullWidth
      variant="outlined"
      placeholder="Buscar factura por número, cliente o ciudad..."
      value={localTerm}
      onChange={(e) => setLocalTerm(e.target.value)}
      sx={{ minWidth: '250px' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        ),
        endAdornment: initialValue ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => onSearchChange('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

const InvoiceList = ({ invoiceStore, erpListsStore, authStore, setNotification: parentSetNotification }) => {
  const theme = useTheme();
  const { supabase } = useSupabaseClient();
  const [state, setState] = useState(invoiceStore.getState());
  const [authState, setAuthState] = useState(authStore ? authStore.getState() : null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [accountingModalOpen, setAccountingModalOpen] = useState(false);
  const [selectedInvoiceForAccounting, setSelectedInvoiceForAccounting] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isSyncingLirium, setIsSyncingLirium] = useState(false);
  const [isApprovingSelected, setIsApprovingSelected] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFileInvoiceTypes, setSelectedFileInvoiceTypes] = useState({});
  const [selectedFileCustomDocTypes, setSelectedFileCustomDocTypes] = useState({});
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [includeInstructions, setIncludeInstructions] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [accordionPages, setAccordionPages] = useState({});
  const [syncYear, setSyncYear] = useState(String(new Date().getFullYear()));
  const [syncMonth, setSyncMonth] = useState(String(new Date().getMonth() + 1));
  const [editingInvoiceNumberId, setEditingInvoiceNumberId] = useState(null);
  const [editingInvoiceNumberValue, setEditingInvoiceNumberValue] = useState('');
  const [isSavingInvoiceNumber, setIsSavingInvoiceNumber] = useState(false);
  const [bulkInvoiceType, setBulkInvoiceType] = useState(() => {
    try {
      const stored = localStorage.getItem(BULK_INVOICE_TYPE_STORAGE_KEY);
      const value = String(stored || '').trim();
      if (value === 'libre' || value === 'servicio' || value === 'inventario' || value === 'cuenta_cobro') return value;
    } catch {
      // ignore
    }
    return 'libre';
  });
  const [bulkCustomDocType, setBulkCustomDocType] = useState(() => {
    try {
      return String(localStorage.getItem(BULK_CUSTOM_DOC_TYPE_STORAGE_KEY) || '').trim();
    } catch { return ''; }
  });
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(() => new Set());
  const [selectMenuAnchor, setSelectMenuAnchor] = useState(null);
  const dropZoneRef = useRef(null);
  const prevSearchTermRef = useRef(state.filters?.searchTerm || '');

  const {
    monthlySummary = [],
    isLoading = false,
    hasMore = false,
    error = null,
    filters = { dateFrom: null, dateTo: null, amountMin: null, amountMax: null, searchTerm: '' },
    sortBy = 'date-desc'
  } = state;

  const loadedInvoices = useMemo(() => {
    return monthlySummary.flatMap(g => g.invoices || []);
  }, [monthlySummary]);

  const getCustomDocTypesForItemType = (itemType) => {
    if (!authState?.user?.enable_custom_doc_types) return [];
    return (authState?.tenantDocTypes || []).filter(d => d.item_type === itemType);
  };

  const { setFilters, resetFilters, setSort } = invoiceStore.getState();

  useEffect(() => {
    if (authStore) {
      authStore.getState().fetchTenantDocTypes();
      const unsubscribeAuth = authStore.subscribe((newAuthState) => {
        setAuthState(newAuthState);
      });
      return () => unsubscribeAuth();
    }
  }, [authStore]);

  // Observer removed. We use lazy loading for each month group.


  const setNotification = parentSetNotification || ((notification) => console.log('Notification:', notification)); // Fallback if prop not passed

  const processDroppedFiles = (incomingFiles) => {
    if (!dropZoneRef.current) return;
    
    // Safety check: sometimes querySelector might fail if component structure changed slightly
    const fileInput = dropZoneRef.current.querySelector('input[type="file"]');
    const acceptAttr = fileInput ? fileInput.accept : ".png,.jpg,.jpeg,.pdf,.zip,.xml,application/zip,application/xml,text/xml";
    const acceptedFileTypes = acceptAttr.split(',');

    const newFiles = Array.from(incomingFiles).filter(file => {
      const fileExt = `.${file.name.split('.').pop().toLowerCase()}`;
      const mimeType = file.type.toLowerCase();
      return acceptedFileTypes.some(acceptedType => {
        const trimmedType = acceptedType.trim().toLowerCase();
        return trimmedType === fileExt || trimmedType === mimeType;
      });
    });

    const uniqueNewFiles = newFiles.filter(file => 
      !selectedFiles.some(existingFile => 
        existingFile.name === file.name && 
        existingFile.size === file.size && 
        existingFile.lastModified === file.lastModified
      )
    );

    if (uniqueNewFiles.length > 0) {
      setSelectedFiles(prevFiles => [...prevFiles, ...uniqueNewFiles]);

      setSelectedFileInvoiceTypes((prev) => {
        const next = { ...prev };
        for (const f of uniqueNewFiles) {
          const key = `${f.name}__${f.size}__${f.lastModified}`;
          if (!next[key]) next[key] = bulkInvoiceType;
        }
        return next;
      });

      setSelectedFileCustomDocTypes((prev) => {
        const next = { ...prev };
        for (const f of uniqueNewFiles) {
          const key = `${f.name}__${f.size}__${f.lastModified}`;
          if (!next[key]) next[key] = bulkCustomDocType;
        }
        return next;
      });
    }

    if (newFiles.length < incomingFiles.length) {
      setNotification({ message: 'Algunos archivos fueron omitidos por no ser de un tipo permitido (PNG, JPG, PDF, ZIP, XML).', severity: 'warning' });
    }
  };

  const handleStartEditInvoiceNumber = (invoice) => {
    setEditingInvoiceNumberId(invoice.id);
    setEditingInvoiceNumberValue(invoice.invoice_number || '');
  };

  const handleCancelEditInvoiceNumber = () => {
    setEditingInvoiceNumberId(null);
    setEditingInvoiceNumberValue('');
  };

  const handleSaveEditInvoiceNumber = async (invoice) => {
    const nextValue = String(editingInvoiceNumberValue || '').trim();
    if (!invoice?.id) return;

    setIsSavingInvoiceNumber(true);
    try {
      const result = await invoiceStore.getState().updateInvoiceNumber(invoice.id, nextValue);
      if (result?.success) {
        setNotification({
          message: `Factura ${invoice.invoice_number || invoice.id} actualizada`,
          severity: 'success',
        });
        setEditingInvoiceNumberId(null);
        setEditingInvoiceNumberValue('');
      } else {
        setNotification({
          message: `Error al actualizar número de factura: ${result?.error?.message || 'Error desconocido'}`,
          severity: 'error',
        });
      }
    } catch (error) {
      setNotification({
        message: `Error al actualizar número de factura: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsSavingInvoiceNumber(false);
    }
  };

  const canApproveInvoice = (invoice) => {
    const isStateValid = (!invoice.estado || invoice.estado === 'CAUSADA' || invoice.estado === 'DESAPROBADO' || invoice.estado === 'ERROR');
    return isStateValid && isInvoiceBalanced(invoice);
  };

  const toggleInvoiceSelected = (invoiceId) => {
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  };

  useEffect(() => {
    setSelectedInvoiceIds((prev) => {
      const allowed = new Set(monthlySummary.flatMap(g => g.invoices).map((inv) => inv.id));
      const next = new Set();
      prev.forEach((id) => {
        if (allowed.has(id)) next.add(id);
      });
      return next;
    });
  }, [monthlySummary]);

  const handleApproveSelectedInvoices = async () => {
    const ids = Array.from(selectedInvoiceIds);
    if (ids.length === 0) {
      setNotification({ message: 'Seleccione al menos una factura.', severity: 'warning' });
      return;
    }

    const allInvoices = monthlySummary.flatMap(g => g.invoices);
    const invoiceById = new Map(allInvoices.map((inv) => [inv.id, inv]));

    const parseInvoiceDate = (dateString) => {
      if (!dateString || typeof dateString !== 'string') return null;
      const parts = dateString.split('-').map((n) => Number(n));
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
      const [year, month, day] = parts;
      return new Date(year, month - 1, day);
    };

    const compareInvoiceForApproval = (a, b) => {
      const da = parseInvoiceDate(a?.date);
      const db = parseInvoiceDate(b?.date);

      if (da && db) {
        const diff = da.getTime() - db.getTime();
        if (diff !== 0) return diff;
      } else if (da && !db) {
        return -1;
      } else if (!da && db) {
        return 1;
      }

      const na = a?.invoice_number ?? '';
      const nb = b?.invoice_number ?? '';
      return String(na).localeCompare(String(nb), undefined, { numeric: true, sensitivity: 'base' });
    };

    const invoicesToProcess = ids
      .map((id) => invoiceById.get(id))
      .filter(Boolean)
      .sort(compareInvoiceForApproval);

    let approvedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;
    const errorMessages = [];

    setIsApprovingSelected(true);
    try {
      for (const invoice of invoicesToProcess) {
        if (!canApproveInvoice(invoice)) {
          skippedCount += 1;
          continue;
        }

        const result = await invoiceStore.getState().updateInvoiceStatus(invoice.id, 'APROBADO');
        if (result?.success) {
          approvedCount += 1;
        } else {
          errorCount += 1;
          errorMessages.push(`${invoice.invoice_number || invoice.id}: ${result?.error?.message || 'Error desconocido'}`);
        }
      }

      if (errorCount === 0) {
        const extra = skippedCount > 0 ? ` (omitidas: ${skippedCount})` : '';
        setNotification({ message: `Facturas aprobadas: ${approvedCount}${extra}`, severity: 'success' });
      } else {
        const extra = skippedCount > 0 ? ` | Omitidas: ${skippedCount}` : '';
        setNotification({ message: `Aprobadas: ${approvedCount} | Errores: ${errorCount}${extra}\n${errorMessages.join('\n')}`, severity: 'warning' });
      }

      setSelectedInvoiceIds(new Set());
    } catch (error) {
      setNotification({ message: `Error al aprobar seleccionadas: ${error.message}`, severity: 'error' });
    } finally {
      setIsApprovingSelected(false);
    }
  };

  const handleApproveSelectedInvoicesClick = () => {
    const count = selectedInvoiceIds.size;
    if (!window.confirm(`¿Está seguro de que desea aprobar ${count} factura${count === 1 ? '' : 's'} seleccionada${count === 1 ? '' : 's'}?`)) {
      return;
    }
    handleApproveSelectedInvoices();
  };

  const handleToggleMonth = (group) => {
    if (!group.isLoaded && !group.isLoading) {
       invoiceStore.getState().fetchInvoicesForMonth(group.key);
    }
    
    if (group.invoices.length > 0) {
      const monthIds = group.invoices.map((inv) => inv.id);
      const allSelected = monthIds.every((id) => selectedInvoiceIds.has(id));
      setSelectedInvoiceIds((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          monthIds.forEach((id) => next.delete(id));
        } else {
          monthIds.forEach((id) => next.add(id));
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const allFilteredIds = loadedInvoices.map(inv => inv.id);
    const currentlySelectedCount = selectedInvoiceIds.size;
    const allFilteredCount = allFilteredIds.length;

    if (currentlySelectedCount === allFilteredCount && allFilteredCount > 0) {
      // Si todos están seleccionados, deseleccionar todos
      setSelectedInvoiceIds(new Set());
    } else {
      // De lo contrario, seleccionar todos los cargados
      setSelectedInvoiceIds(new Set(allFilteredIds));
    }
  };

  const handleExportToExcel = () => {
    const allInvoices = loadedInvoices;
    // Si no hay seleccionadas, preguntar si exportar todas las filtradas
    const invoicesToExport = selectedInvoiceIds.size > 0 
      ? allInvoices.filter(inv => selectedInvoiceIds.has(inv.id))
      : allInvoices;

    if (invoicesToExport.length === 0) {
      setNotification({ message: 'No hay facturas para exportar.', severity: 'warning' });
      return;
    }

    // Por ahora, como no tengo librería de Excel instalada de forma segura para este entorno, 
    // generaré un CSV que es compatible con Excel.
    const headers = ['Fecha', 'Número', 'Proveedor', 'NIT del proveedor', 'Total', 'Estado', 'Tipo'];
    const rows = invoicesToExport.map(inv => [
      inv.date,
      inv.invoice_number,
      inv.billing_name,
      inv.num_identificacion ?? '',
      inv.total,
      inv.estado,
      inv.invoice_type
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(value => `"${value}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `facturas_exportadas_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setNotification({ message: `Exportadas ${invoicesToExport.length} facturas a CSV/Excel.`, severity: 'success' });
  };
  const handleRejectInvoice = async (invoice) => {
    try {
      const success = await invoiceStore.getState().updateInvoiceStatus(invoice.id, 'DESAPROBADO');
      if (success.success) {
        setNotification({
          message: `Factura ${invoice.invoice_number} desaprobada exitosamente`,
          severity: 'success',
        });
      } else {
        setNotification({
          message: `Error al desaprobar la factura: ${success.error?.message || 'Error desconocido'}`,
          severity: 'error',
        });
      }
    } catch (error) {
      setNotification({
        message: `Error al desaprobar la factura: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const handleBulkInvoiceTypeChange = (event) => {
    const nextType = event.target.value;
    setBulkInvoiceType(nextType);
    try {
      localStorage.setItem(BULK_INVOICE_TYPE_STORAGE_KEY, String(nextType));
    } catch {
      // ignore
    }

    setSelectedFileInvoiceTypes((prev) => {
      const next = { ...prev };
      for (const f of selectedFiles) {
        const key = `${f.name}__${f.size}__${f.lastModified}`;
        next[key] = nextType;
      }
      return next;
    });
  };

  const handleBulkCustomDocTypeChange = (event) => {
    const nextType = event.target.value;
    setBulkCustomDocType(nextType);
    try {
      localStorage.setItem(BULK_CUSTOM_DOC_TYPE_STORAGE_KEY, String(nextType));
    } catch {
      // ignore
    }

    setSelectedFileCustomDocTypes((prev) => {
      const next = { ...prev };
      for (const f of selectedFiles) {
        const key = `${f.name}__${f.size}__${f.lastModified}`;
        next[key] = nextType;
      }
      return next;
    });
  };

  const handleSelectedFileCustomDocTypeChange = (file, nextType) => {
    const key = `${file.name}__${file.size}__${file.lastModified}`;
    setSelectedFileCustomDocTypes((prev) => ({ ...prev, [key]: nextType }));
  };

  const handleSelectedFileInvoiceTypeChange = (file, nextType) => {
    const key = `${file.name}__${file.size}__${file.lastModified}`;
    setSelectedFileInvoiceTypes((prev) => ({ ...prev, [key]: nextType }));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if the leave event is not triggered by entering a child element
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
        setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // You can add more specific feedback here if needed, e.g., based on e.dataTransfer.types
    if (!isDraggingOver) setIsDraggingOver(true); // Ensure it's set if missed by enter
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processDroppedFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  // Suscribirse a los cambios del store
  useEffect(() => {
    const unsubscribe = invoiceStore.subscribe((newState) => {
      setState(newState);
    });
    return () => unsubscribe();
  }, [invoiceStore]);

  // Cargar instrucciones y estado del checkbox desde localStorage al montar el componente
  useEffect(() => {
    const savedInstructions = localStorage.getItem('invoiceInstructions');
    const savedIncludeInstructions = localStorage.getItem('invoiceIncludeInstructions');

    if (savedInstructions) {
      setInstructions(savedInstructions);
    }

    if (savedIncludeInstructions !== null) {
      setIncludeInstructions(savedIncludeInstructions === 'true');
    } else if (savedInstructions && savedInstructions.trim()) {
      // Compatibilidad hacia atrás: si había instrucciones guardadas pero no estado explícito, activar checkbox
      setIncludeInstructions(true);
    }
  }, []);

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      processDroppedFiles(event.target.files);
      event.target.value = null; // Allow selecting the same file again if removed and re-added
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prevFiles) => {
      const nextFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      setSelectedFileInvoiceTypes((prev) => {
        const next = { ...prev };
        const removed = prevFiles[indexToRemove];
        if (removed) {
          const key = `${removed.name}__${removed.size}__${removed.lastModified}`;
          delete next[key];
        }
        return next;
      });
      setSelectedFileCustomDocTypes((prev) => {
        const next = { ...prev };
        const removed = prevFiles[indexToRemove];
        if (removed) {
          const key = `${removed.name}__${removed.size}__${removed.lastModified}`;
          delete next[key];
        }
        return next;
      });
      return nextFiles;
    });
  };

  const handleInstructionsChange = (event) => {
    const value = event.target.value;
    setInstructions(value);
    localStorage.setItem('invoiceInstructions', value);
    
    // Auto-activar checkbox si hay texto, desactivar si está vacío
    const shouldInclude = Boolean(value.trim());
    setIncludeInstructions(shouldInclude);
    localStorage.setItem('invoiceIncludeInstructions', shouldInclude.toString());
  };

  const handleIncludeInstructionsChange = (event) => {
    const checked = event.target.checked;
    setIncludeInstructions(checked);
    localStorage.setItem('invoiceIncludeInstructions', checked.toString());
  };
  const sendFilesToWebhook = async (files) => {
    let anyError = false;
    let errorMessages = [];

    const storedUser = localStorage.getItem('invoice_user');
    let userUUID = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        userUUID = user.id;
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    for (const file of files) {
      const fileName = file.name || 'archivo';
      const extension = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase() : '';
      
      let finalFile = file;
      // Forzar mimetype application/zip para archivos zip
      if (extension === 'zip' && file.type !== 'application/zip') {
        finalFile = new File([file], fileName, { type: 'application/zip', lastModified: file.lastModified || Date.now() });
      }

      const formData = new FormData();
      formData.append('files', finalFile);
      formData.append('fileInfo', JSON.stringify([{ nombre: fileName, extension: extension }]));

      // Nuevo campo: tipo de factura (por defecto: libre)
      const key = `${file.name}__${file.size}__${file.lastModified}`;
      const invoiceType = selectedFileInvoiceTypes[key] || 'libre';
      const customDocType = selectedFileCustomDocTypes[key] || '';
      formData.append('invoice_type', invoiceType);
      formData.append('TipoFactura', invoiceType);
      if (customDocType) {
        formData.append('custom_doc_type', customDocType);
      }

      if (userUUID) {
        formData.append('user_uuid', userUUID);
      }

      if (includeInstructions && instructions.trim()) {
        formData.append('Indicaciones', instructions.trim());
      }

      try {
        const response = await fetch('https://panel.asistentesautonomos.com/webhook/cadb9fa9-6438-48a6-b98d-87827be320aa', {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          anyError = true;
          const errorData = await response.text();
          errorMessages.push(`${fileName}: ${response.status} - ${errorData || 'Intente de nuevo.'}`);
        }
      } catch (error) {
        anyError = true;
        errorMessages.push(`${fileName}: ${error.message}`);
      }
    }

    return { anyError, errorMessages };
  };

  const handleSendFiles = async () => {
    if (selectedFiles.length === 0) {
      setNotification({ message: 'Por favor, seleccione al menos un archivo.', severity: 'warning' });
      return;
    }
    setIsSending(true);

    try {
      const storedUser = localStorage.getItem('invoice_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user && user.id && supabase) {
          const isOverrideActive = localStorage.getItem('invoiceIncludeInstructions') === 'true';
          const overrideGastos = isOverrideActive ? (JSON.parse(localStorage.getItem('override_gastos') || 'null')?.account_code || null) : null;
          const overrideImpuestos = isOverrideActive ? (JSON.parse(localStorage.getItem('override_impuestos') || 'null')?.account_code || null) : null;
          const overrideRetenciones = isOverrideActive ? (JSON.parse(localStorage.getItem('override_retenciones') || 'null')?.account_code || null) : null;
          const overrideProveedores = isOverrideActive ? (JSON.parse(localStorage.getItem('override_proveedores') || 'null')?.account_code || null) : null;
          
          await supabase.from('invoice_tenant_fiscal_config')
            .update({
              override_active: isOverrideActive,
              override_gastos: overrideGastos,
              override_impuestos: overrideImpuestos,
              override_retenciones: overrideRetenciones,
              override_proveedores: overrideProveedores
            })
            .eq('tenant_user_id', user.id);
        }
      }
    } catch (e) {
      console.error('Error updating overrides:', e);
    }

    const { anyError, errorMessages } = await sendFilesToWebhook(selectedFiles);

    if (!anyError) {
      setNotification({ message: '¡Éxito! Todos los archivos fueron enviados y están siendo procesados.', severity: 'success' });
    } else {
      setNotification({ message: `Errores encontrados:\n${errorMessages.join('\n')}`, severity: 'error' });
    }

    setIsSending(false);
    setSelectedFiles([]);
    setSelectedFileInvoiceTypes({});
  };

  const handleSyncLirium = async () => {
    const year = parseInt(syncYear, 10);
    const month = parseInt(syncMonth, 10);

    if (!year || !month || month < 1 || month > 12) {
      setNotification({ message: 'Seleccione un año y mes válidos para sincronizar con Lirium.', severity: 'warning' });
      return;
    }

    const now = new Date();
    const selectedMonthStart = new Date(year, month - 1, 1);
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    if (selectedMonthStart > currentMonthStart) {
      setNotification({ message: 'No se puede sincronizar un mes futuro.', severity: 'warning' });
      return;
    }

    const endDay = year === now.getFullYear() && month === now.getMonth() + 1
      ? now.getDate()
      : new Date(year, month, 0).getDate();
    const paddedMonth = String(month).padStart(2, '0');
    const startDate = `${year}-${paddedMonth}-01`;
    const endDate = `${year}-${paddedMonth}-${String(endDay).padStart(2, '0')}`;
    const serviceUrl = new URL('https://aaa-lirium.nyejnm.easypanel.host/api/sincronizar-compras');
    serviceUrl.searchParams.set('start_date', startDate);
    serviceUrl.searchParams.set('end_date', endDate);

    setIsSyncingLirium(true);
    try {
      const response = await fetch(serviceUrl);
      if (!response.ok) {
        throw new Error(`El servicio de Lirium respondió con estado ${response.status}`);
      }

      const result = await response.json();
      const processed = Number(result.procesados || 0);
      const skipped = Number(result.omitidos || 0);
      const errors = Number(result.errores || 0);
      setNotification({
        message: `Sincronización de Lirium completada (${startDate} a ${endDate}). Nuevos: ${processed}. Ya sincronizados/omitidos: ${skipped}. Errores: ${errors}.`,
        severity: errors > 0 ? 'warning' : 'success'
      });
    } catch (error) {
      console.error('Error al sincronizar Lirium:', error);
      setNotification({ message: `No se pudo completar la sincronización con Lirium: ${error.message}`, severity: 'error' });
    } finally {
      setIsSyncingLirium(false);
    }
  };

  const handleFilterClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterMenuAnchor(null);
  };
  
  const handleShowFilters = () => {
    setShowFilters(true);
    handleFilterClose();
  };
  
  const handleSearchChange = (e) => {
    setLocalSearchTerm(e.target.value);
  };
  
  const handleDateFromChange = (e) => {
    setFilters({ dateFrom: e.target.value });
  };
  
  const handleDateToChange = (e) => {
    setFilters({ dateTo: e.target.value });
  };
  
  const handleAmountMinChange = (e) => {
    setFilters({ amountMin: e.target.value });
  };
  
  const handleAmountMaxChange = (e) => {
    setFilters({ amountMax: e.target.value });
  };
  
  const handleSortChange = (e) => {
    setSort(e.target.value);
  };
  
  const formatCurrency = (amount) => {
    if (isDemoAccount(authState?.user)) return formatColombianCurrency(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };
  
  const formatDate = (dateString) => {
    if (isDemoAccount(authState?.user)) return formatColombianDate(dateString);
    if (!dateString) return 'Sin fecha';
    // Split the date string and create date with explicit year, month, day
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return format(date, 'MMM d, yyyy');
  };

  // Manage expanded groups and auto-fetching
  useEffect(() => {
    const wasCleared = prevSearchTermRef.current && !filters.searchTerm;
    prevSearchTermRef.current = filters.searchTerm || '';

    if (!monthlySummary || monthlySummary.length === 0) return;

    if (wasCleared) {
       // Reset expanded groups to just the current month to prevent storm of fetches
       const now = new Date();
       const currentYear = now.getFullYear();
       const currentMonth = now.getMonth() + 1;
       const currentGroupKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
       
       setExpandedGroups(new Set([currentGroupKey]));
       
       const group = monthlySummary.find(g => g.key === currentGroupKey);
       if (group && !group.isLoaded && !group.isLoading) {
          invoiceStore.getState().fetchInvoicesForMonth(currentGroupKey);
       }
       return;
    }

    // Si hay un término de búsqueda, expandimos automáticamente todos los meses devueltos para facilitar la visibilidad
    if (filters.searchTerm) {
      const allKeys = monthlySummary.map(g => g.key);
      setExpandedGroups(prev => {
        // Only update if they differ to avoid loops
        if (allKeys.some(k => !prev.has(k))) {
          return new Set(allKeys);
        }
        return prev;
      });

      monthlySummary.forEach(group => {
        if (!group.isLoaded && !group.isLoading) {
          invoiceStore.getState().fetchInvoicesForMonth(group.key);
        }
      });
    } else {
      // Initialize with current month if none are expanded
      if (expandedGroups.size === 0) {
         const now = new Date();
         const currentYear = now.getFullYear();
         const currentMonth = now.getMonth() + 1;
         const currentGroupKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
         
         const group = monthlySummary.find(g => g.key === currentGroupKey);
         if (group) {
            setExpandedGroups(new Set([currentGroupKey]));
            if (!group.isLoaded && !group.isLoading) {
               invoiceStore.getState().fetchInvoicesForMonth(currentGroupKey);
            }
         }
      } else {
        // Re-fetch automatically any expanded groups that were reset (e.g. by "Filtrar" o "Actualizar")
        monthlySummary.forEach(group => {
          if (expandedGroups.has(group.key) && !group.isLoaded && !group.isLoading) {
            invoiceStore.getState().fetchInvoicesForMonth(group.key);
          }
        });
      }
    }
  }, [monthlySummary, filters.searchTerm]);

  // Toggle group expansion
  const toggleGroup = (key) => {
    const group = monthlySummary.find(g => g.key === key);
    if (group && !group.isLoaded && !group.isLoading && !expandedGroups.has(key)) {
       invoiceStore.getState().fetchInvoicesForMonth(key);
    }
    
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
        setAccordionPages(p => ({ ...p, [key]: 0 }));
      }
      return newSet;
    });
  };

  const handleChangePage = (groupKey, newPage) => {
    setAccordionPages(prev => ({
      ...prev,
      [groupKey]: newPage
    }));
  };

  // Get month name in Spanish
  const getMonthName = (monthNumber) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber - 1] || '';
  };

  const groupedInvoices = monthlySummary.map(g => ({
     ...g,
     year: parseInt(g.key.split('-')[0], 10),
     month: parseInt(g.key.split('-')[1], 10),
  }));

  const handleShowAccounting = (invoice) => {
    if (invoice) {
      setSelectedInvoiceForAccounting({ id: invoice.id, invoice_number: invoice.invoice_number });
      setAccountingModalOpen(true);
    } else { // Called with null to close
      if (selectedInvoiceForAccounting) {
        invoiceStore.getState().updateInvoice(selectedInvoiceForAccounting.id);
      }
      setAccountingModalOpen(false);
      setTimeout(() => {
        setSelectedInvoiceForAccounting(null);
      }, 300); // Allow modal exit transition
    }
  };

  const isInvoiceBalanced = (invoice) => {
    if (!invoice.accounting_entries || invoice.accounting_entries.length === 0) return true;
    const totalDebit = invoice.accounting_entries.reduce((acc, e) => acc + (Number(e.debit) || 0), 0);
    const totalCredit = invoice.accounting_entries.reduce((acc, e) => acc + (Number(e.credit) || 0), 0);
    return Math.abs(totalDebit - totalCredit) < 0.01;
  };

  useEffect(() => {
    if (selectedInvoice && detailModalOpen) {
      const allInvoices = monthlySummary.flatMap(g => g.invoices || []);
      const updatedInvoice = allInvoices.find(inv => inv.id === selectedInvoice.id);
      if (updatedInvoice && updatedInvoice !== selectedInvoice) {
        setSelectedInvoice(updatedInvoice);
      }
    }
  }, [monthlySummary, selectedInvoice, detailModalOpen]);

  const handleDetailModalOpen = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setTimeout(() => {
      setSelectedInvoice(null);
    }, 300); // Allow modal exit transition
  };

  const handleApproveInvoice = async (invoice) => {
    try {
      const success = await invoiceStore.getState().updateInvoiceStatus(invoice.id, 'APROBADO');
      if (success.success) {
        setNotification({ 
          message: `Factura ${invoice.invoice_number} aprobada exitosamente`, 
          severity: 'success' 
        });
      } else {
        setNotification({ 
          message: `Error al aprobar la factura: ${success.error?.message || 'Error desconocido'}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      setNotification({ 
        message: `Error al aprobar la factura: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  const handleInvoiceTypeChange = async (invoice, nextType) => {
    try {
      const success = await invoiceStore.getState().updateInvoiceType(invoice.id, nextType);
      if (!success?.success) {
        setNotification({
          message: `Error al actualizar el tipo de factura: ${success?.error?.message || 'Error desconocido'}`,
          severity: 'error',
        });
      }
    } catch (error) {
      setNotification({
        message: `Error al actualizar el tipo de factura: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const handleInvoiceCustomDocTypeChange = async (invoice, customDocType) => {
    try {
      const success = await invoiceStore.getState().updateInvoiceCustomDocType(invoice.id, customDocType);
      if (!success?.success) {
        setNotification({
          message: `Error al actualizar el documento: ${success?.error?.message || 'Error desconocido'}`,
          severity: 'error',
        });
      } else {
        // Find if this doc type has an associated warehouse
        const docTypeWarehouses = authStore.getState().docTypeWarehouses || [];
        const mapping = docTypeWarehouses.find(w => w.custom_doc_type === customDocType);
        
        if (mapping && mapping.warehouse_code) {
          // Update all items for this invoice with the new warehouse
          const { error } = await supabase
            .from('invoice_items')
            .update({ warehouse_code: mapping.warehouse_code })
            .eq('invoice_id', invoice.id);
            
          if (error) {
            console.error('Error auto-assigning warehouse to items:', error);
            setNotification({
              message: `Error al auto-asignar bodega: ${error.message}`,
              severity: 'warning',
            });
          } else {
             await invoiceStore.getState().updateInvoice(invoice.id);
             setNotification({
              message: `Bodega ${mapping.warehouse_code} auto-asignada a los ítems.`,
              severity: 'success',
            });
          }
        }
      }
    } catch (error) {
      setNotification({
        message: `Error al actualizar el documento: ${error.message}`,
        severity: 'error',
      });
    }
  };


  if (error) {
    return (
      <EmptyState>
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Invoices
        </Typography>
        <Typography variant="body2">
          {error}
        </Typography>
      </EmptyState>
    );
  }

  return (
    <Box>
      {/* Controles de Búsqueda y Filtro */}
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <DebouncedSearchInput 
          initialValue={filters.searchTerm}
          onSearchChange={(val) => setFilters({ searchTerm: val })}
        />
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            onClick={handleApproveSelectedInvoicesClick}
            disabled={selectedInvoiceIds.size === 0 || isApprovingSelected || isLoading}
          >
            {isApprovingSelected
              ? 'Aprobando...'
              : `Aprobar Seleccionadas${selectedInvoiceIds.size > 0 ? ` (${selectedInvoiceIds.size})` : ''}`}
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => setSelectMenuAnchor(e.currentTarget)}
            disabled={loadedInvoices.length === 0}
            endIcon={<ExpandMoreIcon />}
          >
            Seleccionar
          </Button>
          <Menu
            anchorEl={selectMenuAnchor}
            open={Boolean(selectMenuAnchor)}
            onClose={() => setSelectMenuAnchor(null)}
          >
            <MenuItem dense onClick={handleSelectAll}>
              <Checkbox
                checked={selectedInvoiceIds.size === loadedInvoices.length && loadedInvoices.length > 0}
                indeterminate={selectedInvoiceIds.size > 0 && selectedInvoiceIds.size < loadedInvoices.length}
                size="small"
                sx={{ py: 0 }}
              />
              <ListItemText primary="Seleccionar todos los meses" />
            </MenuItem>
            {groupedInvoices.map((group) => {
              const monthIds = group.invoices.map((inv) => inv.id);
              const selectedCount = monthIds.filter((id) => selectedInvoiceIds.has(id)).length;
              return (
                <MenuItem key={group.key} dense onClick={() => handleToggleMonth(group)}>
                  <Checkbox
                    checked={selectedCount === monthIds.length}
                    indeterminate={selectedCount > 0 && selectedCount < monthIds.length}
                    size="small"
                    sx={{ py: 0 }}
                  />
                  <ListItemText primary={`${getMonthName(group.month)} ${group.year}`} />
                </MenuItem>
              );
            })}
          </Menu>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleExportToExcel}
            disabled={loadedInvoices.length === 0}
          >
            Exportar Excel {selectedInvoiceIds.size > 0 ? ` (${selectedInvoiceIds.size})` : ` (${loadedInvoices.length})`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
          >
            Filtrar
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => invoiceStore.getState().fetchMonthlySummary()}
            disabled={isLoading}
          >
            Actualizar
          </Button>
          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={handleFilterClose}
          >
            <MenuItem onClick={handleShowFilters}>Mostrar opciones de filtro</MenuItem>
            <MenuItem onClick={() => { resetFilters(); handleFilterClose(); }}>Restablecer todos los filtros</MenuItem>
          </Menu>
          <FormControl variant="outlined" size="small" sx={{ minWidth: '180px' }}>
            <InputLabel>Ordenar Por</InputLabel>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              label={isDemoAccount(authState?.user) ? 'Ordenar por' : 'Sort By'}
              startAdornment={<SortIcon sx={{ mr: 1 }} />}
            >
              <MenuItem value="date-desc">Fecha (Más reciente)</MenuItem>
              <MenuItem value="date-asc">Fecha (Más antigua)</MenuItem>
              <MenuItem value="amount-desc">Importe (Mayor)</MenuItem>
              <MenuItem value="amount-asc">Importe (Menor)</MenuItem>
            </Select>
          </FormControl>

          {authState?.user?.show_upload_invoice_type_selectors && (
            <FormControl variant="outlined" size="small" sx={{ minWidth: '220px' }}>
              <InputLabel>Tipo (aplicar a todas)</InputLabel>
              <Select
                value={bulkInvoiceType}
                onChange={handleBulkInvoiceTypeChange}
                label="Tipo (aplicar a todas)"
              >
                <MenuItem value="libre">Libre</MenuItem>
                <MenuItem value="servicio">Servicio</MenuItem>
                <MenuItem value="inventario">Inventario</MenuItem>
                <MenuItem value="cuenta_cobro">Cuenta Cobro</MenuItem>
              </Select>
            </FormControl>
          )}
          
          {authState?.user?.show_upload_invoice_type_selectors && authState?.user?.enable_custom_doc_types && bulkInvoiceType !== 'libre' && getCustomDocTypesForItemType(bulkInvoiceType).length > 0 && (
            <FormControl variant="outlined" size="small" sx={{ minWidth: '220px' }}>
              <InputLabel>Documento (aplicar a todas)</InputLabel>
              <Select
                value={bulkCustomDocType}
                onChange={handleBulkCustomDocTypeChange}
                label="Documento (aplicar a todas)"
              >
                <MenuItem value=""><em>Ninguno</em></MenuItem>
                {getCustomDocTypesForItemType(bulkInvoiceType).map(dt => (
                  <MenuItem key={dt.doc_type_code} value={dt.doc_type_code}>
                    {dt.doc_type_code} - {dt.doc_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {/* Sección de Carga de Archivos */}
      <Paper 
        ref={dropZoneRef}
        className="notranslate"
        translate="no"
        elevation={isDraggingOver ? 6 : 2} 
        sx={{
          my: 3, 
          p: 2, 
          border: isDraggingOver ? `2px dashed ${theme.palette.primary.main}` : `2px dashed transparent`,
          backgroundColor: isDraggingOver ? alpha(theme.palette.primary.light, 0.1) : theme.palette.background.paper,
          transition: 'border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Typography variant="h6" gutterBottom>
          Adjuntar Documentos de Factura
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <input
            accept=".png,.jpg,.jpeg,.pdf,.zip,.xml,application/zip,application/xml,text/xml"
            style={{ display: 'none' }}
            id="raised-button-file-invoice"
            multiple
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="raised-button-file-invoice">
            <Button variant="outlined" component="span" fullWidth>
              Seleccionar Archivos (PNG, JPG, PDF, ZIP, XML)
            </Button>
          </label>
          
          {/* Sección de Configuración de Cuentas */}
          <Box sx={{ mt: 2 }}>
            <AccountOverrideForm
              includeInstructions={includeInstructions}
              setIncludeInstructions={setIncludeInstructions}
            />
          </Box>
          {selectedFiles.length > 0 && (
            <Box mt={1}>
              <Typography variant="subtitle2">Archivos seleccionados ({selectedFiles.length}):</Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                {selectedFiles.map((file, index) => (
                  (() => {
                    const fileKey = `${file.name}__${file.size}__${file.lastModified}`;
                    const fileInvoiceType = selectedFileInvoiceTypes[fileKey] || 'libre';
                    return (
                  <ListItem
                    key={fileKey}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {authState?.user?.show_upload_invoice_type_selectors && (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                              value={fileInvoiceType}
                              label="Tipo"
                              onChange={(e) => handleSelectedFileInvoiceTypeChange(file, e.target.value)}
                            >
                              <MenuItem value="libre">Libre</MenuItem>
                              <MenuItem value="servicio">Servicio</MenuItem>
                              <MenuItem value="inventario">Inventario</MenuItem>
                              <MenuItem value="cuenta_cobro">Cuenta Cobro</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        {authState?.user?.show_upload_invoice_type_selectors && authState?.user?.enable_custom_doc_types && fileInvoiceType !== 'libre' && getCustomDocTypesForItemType(fileInvoiceType).length > 0 && (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Doc</InputLabel>
                            <Select
                              value={selectedFileCustomDocTypes[fileKey] || ''}
                              label="Doc"
                              onChange={(e) => handleSelectedFileCustomDocTypeChange(file, e.target.value)}
                            >
                              <MenuItem value=""><em>Ninguno</em></MenuItem>
                              {getCustomDocTypesForItemType(fileInvoiceType).map(dt => (
                                <MenuItem key={dt.doc_type_code} value={dt.doc_type_code}>
                                  {dt.doc_type_code}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <IconButton edge="end" aria-label="delete" onClick={() => removeFile(index)} size="small">
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    }
                    sx={{
                      pl: 1,
                      pr: !authState?.user?.show_upload_invoice_type_selectors
                        ? 6
                        : authState?.user?.enable_custom_doc_types && fileInvoiceType !== 'libre' && getCustomDocTypesForItemType(fileInvoiceType).length > 0
                          ? 38
                          : 22
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2', noWrap: true, title: file.name }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(2)} KB`}
                    />
                  </ListItem>
                    );
                  })()
                ))}
              </List>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendFiles}
            disabled={selectedFiles.length === 0 || isSending || isSyncingLirium}
            fullWidth
          >
            <Box
              component="span"
              aria-hidden={isSending}
              sx={{ display: isSending ? 'none' : 'inline' }}
            >
              Enviar Archivos Seleccionados
            </Box>
            <Box
              component="span"
              aria-hidden={!isSending}
              sx={{ display: isSending ? 'inline-flex' : 'none' }}
            >
              <CircularProgress size={24} color="inherit" />
            </Box>
          </Button>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mt: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSyncLirium}
              disabled={isSyncingLirium}
              sx={{ flexShrink: 0 }}
            >
              {isSyncingLirium ? 'Sincronizando...' : 'Sincronizar Lirium'}
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }} disabled={isSyncingLirium}>
              <InputLabel>Mes</InputLabel>
              <Select
                label="Mes"
                value={syncMonth}
                onChange={(e) => setSyncMonth(e.target.value)}
              >
                <MenuItem value="1">Enero</MenuItem>
                <MenuItem value="2">Febrero</MenuItem>
                <MenuItem value="3">Marzo</MenuItem>
                <MenuItem value="4">Abril</MenuItem>
                <MenuItem value="5">Mayo</MenuItem>
                <MenuItem value="6">Junio</MenuItem>
                <MenuItem value="7">Julio</MenuItem>
                <MenuItem value="8">Agosto</MenuItem>
                <MenuItem value="9">Septiembre</MenuItem>
                <MenuItem value="10">Octubre</MenuItem>
                <MenuItem value="11">Noviembre</MenuItem>
                <MenuItem value="12">Diciembre</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Año"
              type="number"
              size="small"
              value={syncYear}
              onChange={(e) => setSyncYear(e.target.value)}
              disabled={isSyncingLirium}
              sx={{ width: 100 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Panel de Filtros Avanzados (condicional) */}
      {showFilters && (
        <Fade in={showFilters}>
          <FilterPaper elevation={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Filtros</Typography>
              <IconButton size="small" onClick={() => setShowFilters(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                {isDemoAccount(authState?.user) ? <ColombianDateField label="Fecha Desde" fullWidth
                  value={filters.dateFrom || ''} onChangeValue={(value) => handleDateFromChange({target:{value}})} /> : <TextField
                  label="Fecha Desde"
                  type="date"
                  fullWidth
                  value={filters.dateFrom || ''}
                  onChange={handleDateFromChange}
                  InputLabelProps={{ shrink: true }}
                />}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {isDemoAccount(authState?.user) ? <ColombianDateField label="Fecha Hasta" fullWidth
                  value={filters.dateTo || ''} onChangeValue={(value) => handleDateToChange({target:{value}})} /> : <TextField
                  label="Fecha Hasta"
                  type="date"
                  fullWidth
                  value={filters.dateTo || ''}
                  onChange={handleDateToChange}
                  InputLabelProps={{ shrink: true }}
                />}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Importe Mínimo"
                  type="number"
                  fullWidth
                  value={filters.amountMin || ''}
                  onChange={handleAmountMinChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Importe Máximo"
                  type="number"
                  fullWidth
                  value={filters.amountMax || ''}
                  onChange={handleAmountMaxChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
              <Button variant="outlined" onClick={resetFilters}>Restablecer</Button>
            </Box>
          </FilterPaper>
        </Fade>
      )}

      {/* Grouped Invoices Display */}
      <Box sx={{ mt: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        ) : groupedInvoices.length > 0 ? (
          <>
          {groupedInvoices.map((group) => (
            <Accordion
              key={group.key}
              expanded={expandedGroups.has(group.key)}
              onChange={() => toggleGroup(group.key)}
              sx={{ mb: 2, '&:before': { display: 'none' } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {getMonthName(group.month)} {group.year}
                  </Typography>
                  <Chip
                    label={`${group.count} factura${group.count !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2, pb: 1, minHeight: '100px' }}>
                {group.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {group.invoices
                        .slice((accordionPages[group.key] || 0) * 50, ((accordionPages[group.key] || 0) + 1) * 50)
                        .map((invoice) => (
                        <InvoiceCardItem
                          key={invoice.id}
                          invoice={invoice}
                          theme={theme}
                          authState={authState}
                          isSelected={selectedInvoiceIds.has(invoice.id)}
                          isEditingNumber={editingInvoiceNumberId === invoice.id}
                          editingInvoiceNumberValue={editingInvoiceNumberValue}
                          isSavingInvoiceNumber={isSavingInvoiceNumber}
                          formatCurrency={formatCurrency}
                          getStatusColor={getStatusColor}
                          handleInvoiceNumberEditStart={handleStartEditInvoiceNumber}
                          handleInvoiceNumberChange={(e) => setEditingInvoiceNumberValue(e.target.value)}
                          handleInvoiceNumberSave={handleSaveEditInvoiceNumber}
                          handleInvoiceNumberCancel={handleCancelEditInvoiceNumber}
                          handleInvoiceTypeChange={handleInvoiceTypeChange}
                          getCustomDocTypesForItemType={getCustomDocTypesForItemType}
                          handleInvoiceCustomDocTypeChange={handleInvoiceCustomDocTypeChange}
                          toggleInvoiceSelected={toggleInvoiceSelected}
                          handleDetailModalOpen={handleDetailModalOpen}
                          handleShowAccounting={handleShowAccounting}
                          handleRejectInvoice={handleRejectInvoice}
                          handleApproveInvoice={handleApproveInvoice}
                          isInvoiceBalanced={isInvoiceBalanced}
                          handleRetrySync={() => invoiceStore.getState().retryInvoiceSync(invoice.id)}
                        />
                      ))}
                    </Grid>
                    {group.invoices.length > 50 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                        <Pagination
                          count={Math.ceil(group.invoices.length / 50)}
                          page={(accordionPages[group.key] || 0) + 1}
                          onChange={(e, value) => handleChangePage(group.key, value - 1)}
                          color="primary"
                          showFirstButton 
                          showLastButton
                        />
                      </Box>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
          </>
        ) : (
          <EmptyState>
            <SearchIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No se encontraron facturas</Typography>
          </EmptyState>
        )}
      </Box>

      {/* Modals Section */}
      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          invoiceStore={invoiceStore}
          erpListsStore={erpListsStore}
        />
      )}

      {selectedInvoiceForAccounting && (
        <AccountingEntriesModal
          open={accountingModalOpen}
          onClose={() => handleShowAccounting(null)}
          invoiceId={selectedInvoiceForAccounting.id}
          invoiceNumber={selectedInvoiceForAccounting.invoice_number}
          erpListsStore={erpListsStore}
        />
      )}
    </Box>
  );
};

export default InvoiceList;

